import {Button, Form, Icon, Input, InputOnChangeData, Loader, Popup} from "semantic-ui-react"
import {isReady, loading, Loading, loadingIfNull, ready} from "../tsutil"
import {CommonPlace, Place, PlaceNickname} from "../model/places/PlaceTypes"
import {maybePlaceNickname, placeCategory, placeName} from "../model/places/place"
import {PlaceSign} from "./PlaceSign"
import {
  Booker as BOOKER,
  CommonPlaces,
  DeletePlaceNickname as DELETE_PLACE_NICKNAME,
  PlaceNicknames,
  PlaceNicknames as PLACE_NICKNAMES,
  PlaceSuggest as PLACE_SUGGESTIONS,
  UpsertPlaceNickname as UPSERT_PLACE_NICKNAME,
  UpsertPlaceNicknameMutation,
  UUIDObjFragment,
} from "../graphql/queries"
import * as React from "react"
import {Booker} from "../model/BookingTypes"
import {GraphQL, makeQueryWithVarsNoT} from "../graphql"
import {ID} from "../model/id"
import {WithModalStore} from "../App"
import {errorUnionToEither, handleError, OrError} from "../graphql/errors"

function Spacer() {
  return <span style={{display: "inline-block", width: "10px"}} />
}

interface OwnProps extends WithModalStore {
  place: Place
  disabled: boolean
  refetchLocaleId?: ID
  onDelete: (() => void) | null
}

interface State {
  nicknameValue: string | null
}

type Props = OwnProps & CommonPlacesProp & NicknamesProp & BookerProp

export function removeNulls<T>(xs: Array<T | null>): Array<T> {
  const ret = []
  for (const x of xs) {
    if (x !== null) {
      ret.push(x)
    }
  }
  return ret
}

class PlaceComponent extends React.Component<Props, State> {
  private textInput: Input | null = null

  constructor(props: Props) {
    super(props)

    this.state = {
      nicknameValue: null,
    }
  }

  focusInput = () => this.textInput && this.textInput.focus()

  handleEditClick() {
    const {place, placeNicknames} = this.props

    if (!placeNicknames.isSome()) {
      return loading
    }

    return ready(() => {
      if (this.state.nicknameValue === null) {
        const nickname = maybePlaceNickname(placeNicknames.value, place.id)
        const nicknameValue = nickname ? nickname.label : ""
        this.setState(() => ({nicknameValue}), () => this.focusInput())
      } else {
        this.setState({nicknameValue: null})
      }
    })
  }

  handleChange = (e: any, field: InputOnChangeData) => {
    this.setState({nicknameValue: field.value})
  }

  handleSave = async () => {
    const {modalStore, place} = this.props
    const {nicknameValue} = this.state

    if (nicknameValue !== null) {
      if (nicknameValue !== "") {
        await this.onUpsertPlaceNickname({
          id: place.id,
          label: nicknameValue,
        })
        this.setState({nicknameValue: null})
      } else {
        this.props.modalStore.handlePromiseThrow(
          modalStore.applyModal({
            error: (m) => ["Delete place nickname failed", m],
            action: async () => {
              const data = await this.onDeletePlaceNickname()
              return data.map(() => this.setState({nicknameValue: null}))
            },
          }),
        )
      }
    }
  }

  handleCancel = () => this.setState({nicknameValue: null})

  handleNop = () => null

  render() {
    const {place, booker, commonPlaces, placeNicknames, onDelete, disabled} = this.props

    const handleEditClick = this.handleEditClick()
    if (!isReady(commonPlaces) || !isReady(placeNicknames) || !isReady(handleEditClick)) {
      return <Loader inline active />
    }

    const name = placeName(commonPlaces.value, place)
    const nickname = maybePlaceNickname(placeNicknames.value, place.id)
    const category = placeCategory(commonPlaces.value, place)
    const starColor = nickname ? "teal" : "grey"

    const {nicknameValue} = this.state
    const editingNickname = nicknameValue !== null

    const showNicknameStar = !editingNickname && booker

    return (
      <div>
        {onDelete && (
          <Icon
            disabled={disabled}
            link={!disabled}
            onClick={disabled ? this.handleNop : onDelete}
            name="close"
            color="grey"
          />
        )}
        <Spacer />
        <span onClick={handleEditClick.value}>
          <PlaceSign
            nickname={nickname || null}
            pointing="right"
            type={place.type}
            category={category}
          />
        </span>
        <span>{name}</span>
        <Spacer />
        {editingNickname && (
          <Form.Group>
            <span style={{marginTop: "10px", paddingLeft: "0.5em"}}>
              <Input
                action
                size="small"
                ref={(c) => (this.textInput = c)}
                onChange={this.handleChange}
                placeholder="Nickname"
                value={nicknameValue}
              >
                <input />
                <Button
                  color="green"
                  size="small"
                  icon="checkmark"
                  onClick={() => {
                    this.props.modalStore.handlePromiseThrow(this.handleSave())
                  }}
                />
                <Button size="small" icon="remove" onClick={this.handleCancel} />
              </Input>
            </span>
          </Form.Group>
        )}
        {showNicknameStar && (
          <Popup
            trigger={
              <Icon
                disabled={false}
                link={true}
                onClick={handleEditClick.value}
                name="star"
                color={starColor}
              />
            }
            content="Give your favourite places a nickname such as 'Home' or 'Work'"
          />
        )}
      </div>
    )
  }

  static refetchQueries(localeId: ID | null) {
    return removeNulls([
      localeId
        ? makeQueryWithVarsNoT({
            query: PLACE_SUGGESTIONS,
            variables: {localeId, text: ""},
          })
        : null,
      makeQueryWithVarsNoT({
        query: PLACE_NICKNAMES,
        variables: {},
      }),
    ])
  }

  onUpsertPlaceNickname(nickname: PlaceNickname): Promise<UpsertPlaceNicknameMutation> {
    return GraphQL.mutate(UPSERT_PLACE_NICKNAME, nickname, {
      // We want the nickname to appear/disappear from the suggestion list and refetchQueries is
      // heavy handed tool compared to (say) update but it is very simple and because the text
      // is empty the backend doesn't need to go to google...
      refetchQueries: PlaceComponent.refetchQueries(this.props.refetchLocaleId || null),
    })
  }

  async onDeletePlaceNickname(): Promise<OrError<UUIDObjFragment>> {
    const data = await GraphQL.mutate(
      DELETE_PLACE_NICKNAME,
      {id: this.props.place.id},
      {
        refetchQueries: PlaceComponent.refetchQueries(this.props.refetchLocaleId || null),
      },
    )
    return errorUnionToEither(data.deletePlaceNickname)
  }
}

interface BookerProp {
  booker: Booker | null
}

function WithBooker(props: OwnProps & CommonPlacesProp & NicknamesProp) {
  return (
    <GraphQL.Query
      query={BOOKER}
      variables={{}}
      render={({data}) => {
        if (data !== null) {
          return handleError(errorUnionToEither(data.booker), (data) => (
            <PlaceComponent {...props} booker={data.booker} />
          ))
        }
        // This should not happen, as Booker is loaded in InitializeSession
        return <Loader active />
      }}
    />
  )
}

interface NicknamesProp {
  placeNicknames: Loading<Array<PlaceNickname>>
}

function WithPlaceNicknames(props: OwnProps & CommonPlacesProp) {
  return (
    <GraphQL.Query
      query={PlaceNicknames}
      variables={{}}
      render={({data}) => {
        return (
          <WithBooker
            {...props}
            placeNicknames={loadingIfNull(data).map((data) => data.placeNicknames)}
          />
        )
      }}
    />
  )
}

interface CommonPlacesProp {
  commonPlaces: Loading<Array<CommonPlace>>
}

function WithCommonPlaces(props: OwnProps) {
  return (
    <GraphQL.Query
      query={CommonPlaces}
      variables={{}}
      render={({data}) => {
        return (
          <WithPlaceNicknames
            {...props}
            commonPlaces={loadingIfNull(data).map((data) => data.commonPlaces)}
          />
        )
      }}
    />
  )
}

export {WithCommonPlaces as Place}



// WEBPACK FOOTER //
// ./src/components/Place.tsx